import React from 'react';
import { useIntl } from 'react-intl';
import DetailedFields from '@modules/members/enrollments/EnrollmentListing/DetailedFields';
import { formatNamesReplaceSpace } from '@utils/miscellaneous/common.util';
import { DESC_FORMAT_15 } from '@utils/dates/dateFormats.utils';
import { EMPTY_VALUE } from '../../constants';
import { IApplication } from '../../types';
import { safeFormatDate } from '../../utils';

// ============================================================================
// Component
// ============================================================================

interface IApplicationSummarySectionProps {
    application: IApplication;
}

const ApplicationSummarySection: React.FC<IApplicationSummarySectionProps> = ({ application }) => {
    const intl = useIntl();
    const {
        applicationYear = '',
        applicationId = '',
        submissionDate = '',
        primaryContactFirstName,
        primaryContactLastName,
        financial,
        primaryTaxFilerNames
    } = application;

    const primaryTaxFiler = formatNamesReplaceSpace(primaryTaxFilerNames, EMPTY_VALUE);
    const primaryContact = `${primaryContactFirstName ?? ''} ${primaryContactLastName ?? ''}`.trim() 
        || EMPTY_VALUE;
    const financialApplication = financial != null
        ? intl.formatMessage({ id: financial ? 'label.yes' : 'label.no' })
        : EMPTY_VALUE;
    const coverageYearText = applicationYear
        ? `${applicationYear} ${intl.formatMessage({ id: 'historical.member.applications.coverageYear' })}`
        : EMPTY_VALUE;

    return (
        <div className="padding-3 margin-bottom-3 border-1px border-base-lighter radius-md">
            <div className="grid-row">
                <div className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4">
                    <div className={'grid-row margin-y-105'}>
                        <div className={'grid-col-12'}>
                            <span data-testid="coverage-year">
                                {coverageYearText}
                            </span>
                        </div>
                    </div>
                    <DetailedFields
                        label="historical.member.applications.caseNumber"
                        value={applicationId || EMPTY_VALUE}
                        dataTestId="case-number"
                        showColon
                    />
                </div>
                <div className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4">
                    <DetailedFields
                        label="historical.member.applications.primaryTaxFiler"
                        value={primaryTaxFiler}
                        dataTestId="primary-tax-filer"
                        showColon
                    />
                    <DetailedFields
                        label="historical.member.applications.primaryContact"
                        value={primaryContact}
                        dataTestId="primary-contact"
                        showColon
                    />
                </div>
                <div className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4">
                    <DetailedFields
                        label="historical.member.applications.submissionDate"
                        value={safeFormatDate(submissionDate, DESC_FORMAT_15)}
                        dataTestId="submission-date"
                        showColon
                    />
                    <DetailedFields
                        label="historical.member.applications.financialApplication"
                        value={financialApplication}
                        dataTestId="financial-application"
                        showColon
                    />
                </div>
            </div>
        </div>
    );
};

export default ApplicationSummarySection;

